import { deriveLinearConversionFactors } from "../../Units/UnitConversion.js";
import { SensorConversions } from "../conversionFunctions/sensorConversions.js";
import { SensorElementConfig } from "./types.js";

type LinearConfig = SensorElementConfig<Record<string, never>, typeof SensorConversions.linearWithMinAndMax>;
type UnityConfig = SensorElementConfig<Record<string, never>, typeof SensorConversions.unity>;

const { slope, offset } = deriveLinearConversionFactors("miles_per_hour", "meters_per_second");

/**
 * Shared shape for wind speed and wind gust: both are the same physical quantity reported by
 * WeatherLink in mph, so they share a conversion. They do not share a unit category — gust is the
 * interval high of the series wind speed reports the mean of, and the two therefore bucket
 * differently — so each config below sets its own.
 */
const windSpeedConversion: Omit<LinearConfig, "testCases" | "unitCategory"> = {
    legalConvertedRange: [0, null],
    conversionKey: "linearWithMinAndMax",
    parameterConfigs: {},
    buildConversionParams: () => ({
        slope,
        offset,
        outputMin: 0,
        outputMax: Number.MAX_VALUE,
    }),
    decodedUnits: "miles_per_hour",
    convertedUnits: "meters_per_second",
};

export const imperialWindSpeedElementConfig: LinearConfig = {
    ...windSpeedConversion,
    unitCategory: "windSpeed",
    testCases: [
        { parameters: {}, decodedValue: 0, convertedValue: 0 },
        { parameters: {}, decodedValue: 10, convertedValue: 4.4704 },
    ],
};

export const imperialWindGustElementConfig: LinearConfig = {
    ...windSpeedConversion,
    // Its own category rather than `windSpeed`, because each point is already the high over its
    // reporting interval: `windGust` carries `max` as its preferred bucket aggregate, which the
    // mean that `windSpeed` buckets with would wash out.
    unitCategory: "windGust",
    testCases: [
        { parameters: {}, decodedValue: 0, convertedValue: 0 },
        { parameters: {}, decodedValue: 45, convertedValue: 20.1168 },
    ],
};

export const windHeadingElementConfig: UnityConfig = {
    legalConvertedRange: [0, 360],
    conversionKey: "unity",
    parameterConfigs: {},
    buildConversionParams: () => ({}),
    decodedUnits: "degree",
    convertedUnits: "degree",
    unitCategory: "windHeading",
    testCases: [
        { parameters: {}, decodedValue: 0, convertedValue: 0 },
        { parameters: {}, decodedValue: 270, convertedValue: 270 },
    ],
} as const;
